/**
 * UH-FE: inline upload-history table inside the Upload History section
 * of `ChannelDatasetDetail`. Replaces the placeholder caption that
 * shipped with B17-FE.
 *
 * Six columns: Filename, Records, Rejected, Uploaded by, Uploaded at, Status.
 * Status cell wraps the `<Badge>` in a `<Tooltip>` ONLY when the row
 * is `failed` AND carries a non-empty `errorMessage`; the tooltip
 * body shows the message truncated to 140 chars with an ellipsis when
 * longer. Date cell always wraps in a tooltip exposing the raw ISO.
 *
 * Uploader names come from `useUsersById` (the same B15-FE hook the
 * records modal uses), with truncated-uuid fallback when the name
 * lookup misses.
 *
 * Empty-state ("No uploads yet for this channel.") is rendered
 * without firing the fetch — `uploadCount === 0` short-circuits the
 * hook's `enabled` gate.
 */
import React from 'react';

import { Badge } from '../../../../../ui/badge';
import { Button } from '../../../../../ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../../../ui/table';
import { Timestamp } from '../../../../../ui/timestamp';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '../../../../../ui/tooltip';
import { useUsersById } from '../../../annotate/hooks/useUsers';
import type { UserSummary } from '../../../annotate/types';
import { useChannelUploads } from '../../hooks/useDatasets';
import type { UploadHistoryEntry } from '../../types';
import { uploadStatusBadge } from './uploadStatusBadge';


interface Props {
  channelDatasetId: string;
  /** From `dataset.uploadCount`; gates the fetch + the empty-state branch. */
  uploadCount: number;
}

/** Page size — matches BE default. Sized for the inline section
 *  (sparse audit data; tens not thousands per channel). */
export const PAGE_SIZE = 20;

/** Maximum characters surfaced in the failed-row error tooltip before
 *  the body is truncated with an ellipsis. */
export const ERROR_TOOLTIP_CAP = 140;


/** "abcd1234…" — used when uploader name lookup misses. */
const uuidFallback = (id: string): string => `${id.slice(0, 8)}…`;

const uploaderLabel = (
  id: string,
  byId: Record<string, UserSummary> | undefined,
): string => {
  const name = byId?.[id]?.name;
  if (name && name.trim().length > 0) return name;
  return uuidFallback(id);
};

const truncate = (text: string, cap: number): string =>
  text.length > cap ? `${text.slice(0, cap)}…` : text;


const UploadHistoryTable: React.FC<Props> = ({
  channelDatasetId,
  uploadCount,
}) => {
  const [offset, setOffset] = React.useState(0);
  const params = React.useMemo(
    () => ({ limit: PAGE_SIZE, offset }),
    [offset],
  );

  const { data, isLoading, isError, refetch } = useChannelUploads(
    channelDatasetId,
    params,
    { enabled: uploadCount > 0 },
  );

  // Page's unique uploader ids → one batched name-lookup via B15-FE's
  // hook. The hook's own `enabled` guard short-circuits when the
  // array is empty.
  const uploaderIds = React.useMemo(() => {
    const ids = new Set<string>();
    (data?.items ?? []).forEach((r) => {
      if (r.uploadedBy) ids.add(r.uploadedBy);
    });
    return Array.from(ids);
  }, [data?.items]);

  const { data: namesByUserId } = useUsersById(uploaderIds);

  // Empty-state — no fetch fired.
  if (uploadCount === 0) {
    return (
      <p
        className="text-[12px] text-muted-foreground"
        data-testid="upload-history-empty"
      >
        No uploads yet for this channel.
      </p>
    );
  }

  if (isLoading) {
    return (
      <div
        className="py-6 text-center text-[12px] text-muted-foreground"
        data-testid="upload-history-loading"
      >
        Loading uploads…
      </div>
    );
  }

  if (isError) {
    return (
      <div
        className="py-6 text-center text-[12px] text-rose-600"
        data-testid="upload-history-error"
      >
        <p>Failed to load upload history.</p>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="mt-3"
          onClick={() => refetch()}
          data-testid="upload-history-retry"
        >
          Try again
        </Button>
      </div>
    );
  }

  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;
  const canPrev = offset > 0;
  const canNext = offset + PAGE_SIZE < total;

  return (
    <div className="space-y-3">
      <Table className="table-fixed" data-testid="upload-history-table">
        <TableHeader>
          <TableRow>
            <TableHead className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
              Filename
            </TableHead>
            <TableHead className="w-[100px] text-right text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
              Records
            </TableHead>
            {/* UH2/UH3: dedicated column so the zero-error case is
                positively confirmed — a clean upload reads "0" in
                muted grey rather than absence-of-subtitle (which
                operators read as "no information" rather than "no
                errors"). */}
            <TableHead className="w-[100px] text-right text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
              Rejected
            </TableHead>
            <TableHead className="w-[160px] text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
              Uploaded by
            </TableHead>
            <TableHead className="w-[180px] text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
              Uploaded at
            </TableHead>
            <TableHead className="w-[120px] text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
              Status
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {(data?.items ?? []).map((row: UploadHistoryEntry) => {
            const badge = uploadStatusBadge(row.status, row.recordsRejected);
            const showErrorTooltip =
              row.status === 'failed' &&
              !!row.errorMessage &&
              row.errorMessage.trim().length > 0;
            return (
              <TableRow
                key={row.id}
                data-testid={`upload-history-row-${row.id}`}
                title={row.id}
              >
                <TableCell
                  className="max-w-[420px] truncate text-[13px] text-foreground"
                  title={row.filename}
                >
                  {row.filename}
                </TableCell>
                <TableCell className="text-right text-[12px] tabular-nums text-foreground">
                  {row.recordsAdded.toLocaleString()}
                </TableCell>
                {/* UH2/UH3: rejected count as its own column. Always
                    renders — `0` muted, `> 0` rose-600. (Not the
                    destructive red used for FAILED status badges; the
                    import itself succeeded.) Per-row reasons remain
                    toast-only. */}
                <TableCell
                  className={
                    row.recordsRejected > 0
                      ? "text-right text-[12px] tabular-nums text-rose-600"
                      : "text-right text-[12px] tabular-nums text-muted-foreground"
                  }
                  data-testid={`upload-history-rejected-${row.id}`}
                >
                  {row.recordsRejected.toLocaleString()}
                </TableCell>
                <TableCell
                  className="text-[12px] text-muted-foreground"
                  data-testid={`upload-history-uploader-${row.id}`}
                >
                  {uploaderLabel(row.uploadedBy, namesByUserId)}
                </TableCell>
                <TableCell
                  className="text-[12px] text-muted-foreground"
                  data-iso={row.uploadedAt}
                >
                  <Timestamp value={row.uploadedAt} format="absolute" />
                </TableCell>
                <TableCell
                  data-testid={`upload-history-status-${row.id}`}
                  data-error-message={
                    showErrorTooltip ? row.errorMessage : undefined
                  }
                >
                  {showErrorTooltip ? (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="cursor-help">
                          <Badge variant={badge.variant}>{badge.label}</Badge>
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>
                        {truncate(row.errorMessage ?? '', ERROR_TOOLTIP_CAP)}
                      </TooltipContent>
                    </Tooltip>
                  ) : (
                    <Badge variant={badge.variant}>{badge.label}</Badge>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      <div className="flex items-center justify-between">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
          disabled={!canPrev}
          data-testid="upload-history-prev"
        >
          Previous
        </Button>
        <span
          className="text-xs text-muted-foreground"
          data-testid="upload-history-page-label"
        >
          Page {currentPage} of {totalPages}
        </span>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setOffset((o) => o + PAGE_SIZE)}
          disabled={!canNext}
          data-testid="upload-history-next"
        >
          Next
        </Button>
      </div>
    </div>
  );
};

export default UploadHistoryTable;
